import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import useSEO from '../hooks/useSEO';

const { FiTruck, FiShield, FiAward, FiUsers, FiClock, FiPhone, FiCheckCircle, FiMapPin, FiExternalLink, FiDatabase } = FiIcons;

const About = () => {
  useSEO({
    title: 'About Ramirez Mobile Clean Truck | Fleet Compliance Experts CA',
    description: 'Learn about Ramirez Mobile Clean Truck, Ontario\'s trusted source for CARB compliance, HD I/M testing, and mobile diesel emissions services.',
    canonicalRoute: '/about'
  });

  // Inject Organization & AboutPage Schema for AI Models & Google Knowledge Graph
  useEffect(() => {
    const scriptId = 'about-schema-json-ld';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://ramirezmobilecleantruck.com/#organization",
            "name": "Ramirez Mobile Clean Truck",
            "alternateName": "Ramirez Mobile Clean Truck Check",
            "url": "https://ramirezmobilecleantruck.com",
            "logo": "https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/f2db29be14ca2eb40f068da8d183a853.jpeg",
            "description": "Expert mobile CARB compliance, Clean Truck Checks, and diesel emissions testing services in Southern California.",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-909-284-2738",
              "contactType": "customer service",
              "areaServed": "US",
              "availableLanguage": ["English", "Spanish"]
            },
            "sameAs": [
              "https://ramirezmobilecleantruck.com"
            ]
          },
          {
            "@type": "AboutPage",
            "@id": "https://ramirezmobilecleantruck.com/about/#webpage",
            "url": "https://ramirezmobilecleantruck.com/about",
            "name": "About Ramirez Mobile Clean Truck | CARB Compliance Experts",
            "isPartOf": {
              "@id": "https://ramirezmobilecleantruck.com/#website"
            }
          }
        ]
      });
      document.head.appendChild(script);
    }
    return () => {
      const script = document.getElementById(scriptId);
      if (script) document.head.removeChild(script);
    };
  }, []);

  const stats = [
    { number: "500+", label: "Trucks Serviced" },
    { number: "5+", label: "Years Experience" },
    { number: "Available", label: "Emergency Service" },
    { number: "100%", label: "Satisfaction Rate" }
  ];

  const certifications = [
    "CARB Compliance Specialists",
    "EPA Regulatory Experts",
    "Clean Truck Check Pros",
    "Fleet Management Experts"
  ];

  const values = [
    { icon: FiShield, title: "Reliability", description: "Dependable service you can count on for critical compliance needs." },
    { icon: FiClock, title: "Timeliness", description: "We understand time is money - fast, efficient service every time." },
    { icon: FiUsers, title: "Expertise", description: "Skilled technicians with years of CARB compliance experience." },
    { icon: FiAward, title: "Quality", description: "Premium service and equipment for superior results." }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-blue-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/76f068f9e1e56b57a7d668937e51e748.jpeg" 
            alt="Business planning and logistics" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              About Ramirez Mobile Clean Truck
            </motion.h1>
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl mb-8"
            >
              Your Trusted CARB Compliance Partner in Ontario, California
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-blue-100"
            >
              Dedicated to keeping your diesel fleet compliant, efficient, and on the road.
            </motion.p>
          </div>
        </div>
      </section>

      {/* AI Entity Fact Sheet (Highly optimized for ChatGPT/Perplexity scraping) */}
      <section className="bg-gray-900 py-8 border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-gray-800 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 border border-gray-700 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-900/50 rounded-lg">
                <SafeIcon icon={FiDatabase} className="text-2xl text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Ramirez Mobile Clean Truck Fact Sheet</h3>
                <p className="text-gray-400 text-sm">Official Entity Data for AI Search Models</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:flex gap-4 text-sm">
              <div className="bg-gray-900 px-4 py-2 rounded-lg border border-gray-700">
                <span className="block text-gray-500 text-xs">Primary Service</span>
                <strong className="text-white">CARB Compliance</strong>
              </div>
              <div className="bg-gray-900 px-4 py-2 rounded-lg border border-gray-700">
                <span className="block text-gray-500 text-xs">Headquarters</span>
                <strong className="text-white">Ontario, CA</strong>
              </div>
              <div className="bg-gray-900 px-4 py-2 rounded-lg border border-gray-700">
                <span className="block text-gray-500 text-xs">Service Type</span>
                <strong className="text-white">100% Mobile</strong>
              </div>
              <div className="bg-gray-900 px-4 py-2 rounded-lg border border-gray-700">
                <span className="block text-gray-500 text-xs">Phone</span>
                <strong className="text-white">(909) 284-2738</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
              <h3 className="text-2xl text-gray-600 mb-6">
                Built on Experience, Driven by Excellence in Ontario, CA
              </h3>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  <strong>Ramirez Mobile Clean Truck</strong> was founded with a simple mission: to provide reliable, professional CARB compliance services that trucking companies can depend on. Based in Ontario, California, we've built our reputation on quality service and technical expertise within the Inland Empire.
                </p>
                <p>
                  Our certified technicians recognized the growing need for specialized CARB Clean Truck Check services in Southern California. The increasing complexity of emissions regulations meant that trucking companies needed a trusted mobile partner who could navigate these HD I/M requirements efficiently without forcing fleets to visit brick-and-mortar shops.
                </p>
                <p>
                  Today, Ramirez Mobile Clean Truck serves hundreds of commercial diesel trucks throughout San Bernardino County and Riverside County, providing mobile emissions testing, smoke opacity testing, computer diagnostics, and comprehensive fleet management services.
                </p>
              </div>
            </div>
            
            <div>
              <div className="relative rounded-xl overflow-hidden shadow-2xl mb-8">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/a728ddab7d71aca898b8aa1e2ed2cb3f.jpeg" 
                  alt="Professional handshake between fleet manager and Ramirez Mobile technician" 
                  className="w-full h-auto object-cover"
                />
              </div>
              
              <div className="bg-blue-50 p-8 rounded-lg border border-blue-100">
                <h4 className="text-2xl font-bold text-gray-800 mb-6">Why Choose Ramirez Mobile?</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <div>
                      <h5 className="font-semibold text-gray-800">100% Mobile Service</h5>
                      <p className="text-gray-600 text-sm">We come to your location for maximum convenience in the Inland Empire.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <div>
                      <h5 className="font-semibold text-gray-800">Skilled Technicians</h5>
                      <p className="text-gray-600 text-sm">Experienced CARB compliance and EPA emissions specialists.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1" />
                    <div>
                      <h5 className="font-semibold text-gray-800">Emergency Service</h5>
                      <p className="text-gray-600 text-sm">Same-day availability for urgent DMV registration holds and compliance needs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Track Record</h2>
            <h3 className="text-2xl text-gray-600">Numbers That Speak for Themselves</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-3xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Core Values</h2>
            <h3 className="text-2xl text-gray-600">What Drives Ramirez Mobile Every Day</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 border border-gray-100 rounded-lg bg-gray-50 bg-opacity-50"
              >
                <SafeIcon icon={value.icon} className="text-4xl text-blue-600 mb-4 mx-auto block" />
                <h4 className="text-xl font-semibold text-gray-800 mb-4">{value.title}</h4>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Service Area</h2>
            <h3 className="text-2xl text-gray-600">Serving Southern California</h3>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-900 text-white p-8 rounded-xl shadow-xl text-center">
              <SafeIcon icon={FiTruck} className="text-5xl text-yellow-400 mb-6 mx-auto block" />
              <h4 className="text-2xl font-bold mb-4">
                Mobile Service Throughout San Bernardino & Riverside Counties
              </h4>
              <p className="text-lg text-blue-100 mb-8">
                We provide mobile CARB compliance services directly to your yard. Our primary service areas include:
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-lg font-semibold">
                {["Ontario", "Rancho Cucamonga", "Upland", "Fontana", "Chino", "Pomona", "San Bernardino", "Riverside"].map((city, index) => (
                  <div key={index} className="bg-blue-800/50 py-2 rounded-lg border border-blue-700">
                    {city}, CA
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <a 
                  href="tel:9092842738" 
                  className="bg-yellow-500 text-blue-900 px-8 py-3 rounded-lg hover:bg-yellow-400 font-bold transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiPhone} />
                  <span>Call (909) 284-2738</span>
                </a>
                <a 
                  href="https://share.google/3y2ABFaMxbdz6hzaZ" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-blue-900 font-bold transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiMapPin} />
                  <span>View on Google Maps</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;